import { NavLink } from "./NavLink";
import { useState, useEffect, useMemo, useRef, type KeyboardEvent, type MouseEvent } from "react";
import { useLocation, useNavigate } from "react-router";
import { Avatar } from "@/components/ui/Avatar";
import { SettingsPopover } from "../SettingsPopover";
import { useModulePermissions } from "@/context/ModulePermissionsContext";
import { useAuth } from "@/context/AuthContext";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faTableColumns,
  faFilters,
  faChartMixed,
  faFolder,
  faGrid2,
  faChevronDown,
  faRocket,
  faUsers,
  faThumbtack,
  faPlus,
  faServer,
  faFileLines,
} from "@awesome.me/kit-70c1f0055f/icons/classic/regular";
import { faThumbtack as faThumbtackSolid } from "@awesome.me/kit-70c1f0055f/icons/classic/solid";
import { CollapseIcon } from "./CollapseIcon";
import type { Theme } from "@/config/themes"; // Import Theme type from config
import { shouldShowAppsSection } from "@/config/modules";
import { getSpecializedAppsByPermission } from "@/config/apps";
import { getPodIconFromTemplate } from "@/utils/podUtils";
import WorkerAvatar from "@/components/worker/WorkerAvatar";
import { useWorkerList } from "@/context/WorkerListContext";
import { useWorkersPinned } from "@/services/api/worker-ani/queries/useWorkersPinned";
import { usePodList } from "@/context/PodListContext";
import { GlobalSearchButton } from "@/components/global-search/GlobalSearchButton";
import { useOsNavigate } from "@/components/ui/os-layout";

function activateOnEnterOrSpace(
  event: KeyboardEvent,
  action: () => void,
): void {
  if (event.key === "Enter" || event.key === " ") {
    event.preventDefault();
    action();
  }
}

// --- Theme Interface (removed local definition, using import) ---
interface NavigationProps {
  availableThemes?: Theme[];
  currentTheme?: Theme;
  onSelectTheme?: (theme: Theme) => void;
  closeIcon?: React.ReactNode;
  companyId?: string; // Add companyId prop
  forceExpanded?: boolean; // Force navigation to be expanded (used for mobile)
  onToggleLayout?: () => void;
  /** When true, do not rewrite `/` or single-app URLs — aniOS owns the browser URL. */
  isOsShell?: boolean;
}
// --- End of Theme Interface ---

const routes = [
  {
    path: "/dashboard",
    label: "Dashboard",
    icon: <FontAwesomeIcon icon={faTableColumns} fixedWidth />,
  },
  {
    path: "/screener",
    label: "Screener",
    icon: <FontAwesomeIcon icon={faFilters} fixedWidth />,
  },
  {
    path: "/playground",
    label: "Playground",
    icon: <FontAwesomeIcon icon={faChartMixed} fixedWidth />,
  },
  // {
  //   path: "/report-builder",
  //   label: "Report Builder",
  //   icon: <FontAwesomeIcon icon={faCubes} fixedWidth />,
  // },
  {
    path: "/reports",
    label: "Reports",
    icon: <FontAwesomeIcon icon={faFileLines} fixedWidth />,
  },
  {
    path: "/documents",
    label: "Documents",
    icon: <FontAwesomeIcon icon={faFolder} fixedWidth />,
  },
  {
    path: "/ops",
    label: "Operations",
    icon: <FontAwesomeIcon icon={faServer} fixedWidth />,
  },
];

// Worker skeleton component for loading state
const WorkerSkeleton = ({ collapsed }: { collapsed: boolean }) => {
  if (collapsed) {
    return (
      <div className="flex items-center gap-3 px-2.5 min-h-[32px] animate-pulse">
        <div className="w-6 h-6  bg-white/30 rounded-full animate-pulse" />
      </div>
    );
  }

  return (
    <div className="flex items-center gap-2 px-[6px] min-h-[32px]">
      <div className="w-6.5 h-6.5 bg-white/20 rounded-full animate-pulse" />
      <div className="flex-1 ">
        <div
          className="h-3 bg-white/10 rounded-lg animate-pulse"
          style={{ width: "60%" }}
        />
      </div>
    </div>
  );
};

// Pod skeleton component for loading state
const PodSkeleton = ({ collapsed }: { collapsed: boolean }) => {
  if (collapsed) {
    return (
      <div className="flex items-center gap-3 px-2.5 min-h-[32px] animate-pulse">
        <div className="w-6 h-6 bg-slate-300 dark:bg-slate-600 rounded-full animate-pulse" />
      </div>
    );
  }

  return (
    <div className="flex items-center gap-2 px-[6px] min-h-[32px]">
      <div className="w-6.5 h-6.5 bg-slate-500/50 rounded-full animate-pulse" />
      <div className="flex-1 ">
        <div
          className="h-3 bg-slate-500/30 rounded-lg animate-pulse"
          style={{ width: "60%" }}
        />
      </div>
    </div>
  );
};

export function Navigation({
  availableThemes,
  currentTheme,
  onSelectTheme,
  closeIcon,
  companyId,
  forceExpanded = false,
  onToggleLayout,
  isOsShell = false,
}: NavigationProps) {
  const location = useLocation();
  const navigate = useNavigate();
  const osNavigate = useOsNavigate();
  const { hasPermission, permissions } = useModulePermissions();
  const { user } = useAuth();

  // Always start expanded by default, only collapse if saved in localStorage
  const [isCollapsedState, setIsCollapsed] = useState(() => {
    // Try to get the saved state from localStorage
    const savedState = localStorage.getItem("navbarCollapsed");
    // If there's a saved state, use it, otherwise default to false (expanded)
    return savedState ? JSON.parse(savedState) : false;
  });

  // If forceExpanded is true (e.g., mobile view), always show as expanded
  const isCollapsed = forceExpanded ? false : isCollapsedState;
  // const [isProjectsExpanded, setIsProjectsExpanded] = useState(true);
  const [isAppsExpanded, setIsAppsExpanded] = useState(true);
  const [isPodsExpanded, setIsPodsExpanded] = useState(true);
  const [isWorkersExpanded, setIsWorkersExpanded] = useState(true);
  const [isCollapseHovered, setIsCollapseHovered] = useState(false);

  // Pod list state from context
  const {
    pods,
    loading: podLoading,
    error: podError,
    pinnedPods,
    togglePodPin,
  } = usePodList();

  // Worker cards state from context
  const { toggleWorkerPin } = useWorkerList();

  const {
    data: pinnedWorkerCards = [],
    isLoading: pinnedWorkersLoading,
    error: pinnedWorkersError,
  } = useWorkersPinned();

  const podTemplatesToShow = useMemo(() => {
    if (podLoading || podError) {
      return [];
    }
    // Only show pinned pods in the navigation
    return pods.filter((pod) => pinnedPods.includes(pod._id));
  }, [pods, pinnedPods, podLoading, podError]);

  const workersToShow = useMemo(() => {
    if (pinnedWorkersLoading || pinnedWorkersError) {
      return [];
    }
    return pinnedWorkerCards;
  }, [pinnedWorkerCards, pinnedWorkersLoading, pinnedWorkersError]);

  const handlePodsHeaderClick = () => {
    osNavigate("/pod/directory", "Pods");
  };

  const handlePodsIconClick = (e: MouseEvent) => {
    e.stopPropagation();
    setIsPodsExpanded(!isPodsExpanded);
  };

  const handleWorkersHeaderClick = () => {
    osNavigate("/worker/directory", "Workers");
  };

  const handleWorkersIconClick = (e: MouseEvent) => {
    e.stopPropagation();
    setIsWorkersExpanded(!isWorkersExpanded);
  };

  // Effect to handle single app mode and mounting
  useEffect(() => {
    if (permissions?.single_app) {
      setIsCollapsed(true);
    } else if (!forceExpanded) {
      // Only restore from localStorage if not in single app mode and not forced expanded
      const savedState = localStorage.getItem("navbarCollapsed");
      if (savedState) {
        setIsCollapsed(JSON.parse(savedState));
      } else {
        setIsCollapsed(false);
      }
    }
  }, [permissions?.single_app, forceExpanded]);

  // Effect to save collapsed state to localStorage
  useEffect(() => {
    if (!permissions?.single_app) {
      localStorage.setItem("navbarCollapsed", JSON.stringify(isCollapsedState));
    }
  }, [isCollapsedState, permissions?.single_app]);

  // Effect to ensure navigation stays collapsed in single app mode
  useEffect(() => {
    if (isOsShell) {
      return;
    }
    if (permissions?.single_app && permissions.modules.length > 0) {
      // Get the first module and navigate to it
      const firstModule = permissions.modules[0];
      const modulePath = firstModule.replace(/_/g, "-");
      if (!location.pathname.startsWith(`/${modulePath}`)) {
        navigate(`/${modulePath}`, { replace: true });
      }
    } else if (location.pathname === "/") {
      // Only navigate to dashboard if not in single app mode
      navigate("/dashboard", { replace: true });
    }
  }, [permissions, location.pathname, navigate, isOsShell]);

  // Filter routes based on permissions and single app mode
  const permissionedRoutes = useMemo(() => {
    if (!permissions) return [];
    if (permissions.single_app) return [];

    // Create a stable reference to the routes array
    const stableRoutes = [...routes];
    return stableRoutes.filter((route) => {
      const module = route.path.replace(/^\//, "");
      return hasPermission(module);
    });
  }, [permissions, hasPermission]);

  // Memoize the navigation items to prevent unnecessary re-renders
  const navigationItems = useMemo(() => {
    if (!permissions) return null;
    return permissionedRoutes.map((route) => (
      <NavLink
        key={route.path}
        to={route.path}
        icon={route.icon}
        label={route.label}
        isActive={
          route.path === "/"
            ? location.pathname === "/"
            : location.pathname.startsWith(route.path)
        }
        collapsed={isCollapsed}
        themeBaseTextColor={currentTheme?.baseTextColor}
      />
    ));
  }, [
    permissionedRoutes,
    location.pathname,
    isCollapsed,
    permissions,
    currentTheme?.baseTextColor,
  ]);

  /** Classic sidebar Apps section — derived from MODULE_REGISTRY (see apps.specializedApps). */
  const permissionedSpecializedApps = useMemo(
    () => getSpecializedAppsByPermission(hasPermission),
    [hasPermission],
  );

  const scrollableRef = useRef<HTMLDivElement>(null);

  if (!permissions) {
    return null;
  }

  return (
    <div
      className={
        "flex flex-col h-full group/collapse dark:border-gray-800 relative transition-all duration-300"
      }
      style={{ width: isCollapsed ? 51 : 224 }}
    >
      <nav className="flex flex-col h-full">
        {/* User Avatar in Single App Mode */}
        {permissions?.single_app && user && (
          <div className="p-4 pr-0 flex justify-center relative top-4 flex-grow">
            <Avatar
              seed={user.name || user.email}
              size="sm"
              avatarStyle="initials"
            />
          </div>
        )}
        {/* SCROLLABLE AREA: everything except settings/controls */}
        <div className="relative flex-1 flex flex-col min-h-0">
          {/* Scrollable content */}
          <div
            ref={scrollableRef}
            className="flex-1 min-h-0 flex flex-col overflow-y-auto no-scrollbar pb-10"
            style={{
              maskImage: `linear-gradient(to bottom, 
                transparent 0px,
                black 30px,
                black calc(100% - 80px),
                transparent 100%
              )`,
              WebkitMaskImage: `linear-gradient(to bottom, 
                transparent 0px,
                black 30px,
                black calc(100% - 80px),
                transparent 100%
              )`,
            }}
          >
            {/* Main Navigation Items at top level */}
            {!permissions?.single_app && (
              <div className={`p-4 flex flex-col pr-0 pt-8`}>
                {/* Global Search Button */}
                <GlobalSearchButton collapsed={isCollapsed} className="mb-4" />
                {navigationItems}
              </div>
            )}

            {/* PODS Section */}
            {!permissions.single_app && hasPermission("pod") && (
              <div className="">
                <div className={`px-4 py-1 pb-0 pt-0 pr-0 group/icons`}>
                  <div
                    role="button"
                    tabIndex={0}
                    className={`relative flex items-center gap-3 px-2.5 min-h-[32px] rounded-lg group cursor-pointer ${isCollapsed ? " pr-0" : ""
                      } ${currentTheme?.baseTextColor === "light"
                        ? "text-white/60 hover:text-white"
                        : "text-slate-500 hover:text-slate-800"
                      }`}
                    onClick={handlePodsHeaderClick}
                    onKeyDown={(e) => activateOnEnterOrSpace(e, handlePodsHeaderClick)}
                    title="PODS"
                  >
                    <div
                      role="button"
                      tabIndex={0}
                      className="flex-shrink-0 relative w-5 self-center flex items-center justify-center icon-clickable"
                      onClick={handlePodsIconClick}
                      onKeyDown={(e) =>
                        activateOnEnterOrSpace(e, () =>
                          setIsPodsExpanded(!isPodsExpanded),
                        )
                      }
                    >
                      <div className="absolute text-lg">
                        <div className="relative ">
                          {!isCollapsed && (
                            <FontAwesomeIcon
                              icon={faRocket}
                              className={`w-5 h-5 ${isCollapsed ? "" : "transition-opacity"
                                } ${isPodsExpanded && !isCollapsed
                                  ? " opacity-0"
                                  : "opacity-100"
                                }
                              ${!isPodsExpanded
                                  ? "opacity-100 group-hover/icons:opacity-0"
                                  : ""
                                }
                              `}
                              fixedWidth
                            />
                          )}
                          {!isCollapsed && (
                            <FontAwesomeIcon
                              icon={faChevronDown}
                              className={`absolute flex items-center justify-center inset-0 left-1 opacity-0 group-hover:opacity-100 text-xs transition-all ${isPodsExpanded
                                ? "rotate-180 opacity-100 top-2"
                                : "top-1.5"
                                }`}
                              fixedWidth
                            />
                          )}
                        </div>
                      </div>
                    </div>
                    {!isCollapsed && (
                      <div
                        className={`${currentTheme?.baseTextColor === "light"
                          ? "text-white/60"
                          : "text-slate-500"
                          } flex-1 min-w-0 overflow-hidden transition-opacity duration-400 opacity-100`}
                      >
                        <span className="relative top-[-1px] text-[11px] font-mono uppercase tracking-widest inline-block whitespace-nowrap">
                          PODS
                        </span>
                      </div>
                    )}
                  </div>
                </div>

                {(isPodsExpanded || isCollapsed) && (
                  <div className="flex-grow mb-4">
                    <div
                      className={`h-full overflow-y-auto scrollbar-thin scrollbar-thumb-gray-300 dark:scrollbar-thumb-gray-700 px-4 pr-0 flex flex-col `}
                    >
                      {podLoading && (
                        <>
                          <PodSkeleton collapsed={isCollapsed} />
                          <PodSkeleton collapsed={isCollapsed} />
                          <PodSkeleton collapsed={isCollapsed} />
                        </>
                      )}
                      {podError && (
                        <div className="text-xs text-white/60 pl-7 py-1">
                          {podError}
                        </div>
                      )}
                      {!podLoading &&
                        !podError &&
                        podTemplatesToShow.length === 0 && (
                          <NavLink
                            to="/pod/directory"
                            label="Explore Pods"
                            isActive={location.pathname.startsWith(
                              "/pod/directory"
                            )}
                            collapsed={isCollapsed}
                            icon={
                              <div className="w-6 h-6 rounded-full bg-slate-500/50 flex items-center justify-center">
                                <FontAwesomeIcon
                                  icon={faPlus}
                                  className="w-3 h-3 text-white"
                                />
                              </div>
                            }
                            themeBaseTextColor={currentTheme?.baseTextColor}
                          />
                        )}
                      {!podLoading &&
                        !podError &&
                        podTemplatesToShow.length > 0 &&
                        podTemplatesToShow.map((template) => {
                          const isPinned = pinnedPods.includes(
                            (template._id)
                          );
                          return (
                            <div
                              key={(template._id)}
                              className="relative group/navitem"
                            >
                              <NavLink
                                to={`/pod/${(template._id)}`} // Adjust path as needed
                                label={template.name}
                                isActive={location.pathname.startsWith(
                                  `/pod/${(template._id)}`
                                )}
                                collapsed={isCollapsed}
                                rightIcon={
                                  !isCollapsed && (
                                    <div
                                      role="button"
                                      tabIndex={0}
                                      className={`transition-opacity cursor-pointer p-1 rounded ${isPinned
                                        ? "opacity-100"
                                        : "opacity-0 group-hover/navitem:opacity-100"
                                        } ${currentTheme?.baseTextColor === "light"
                                          ? "text-white/60 hover:text-white"
                                          : "text-slate-500 hover:text-slate-800"
                                        }`}
                                      onClick={async (e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        try {
                                          await togglePodPin(
                                            (template._id)
                                          );
                                        } catch (error) {
                                          console.error(
                                            "Failed to toggle pod pin:",
                                            error
                                          );
                                        }
                                      }}
                                      onKeyDown={(e) => {
                                        if (e.key !== "Enter" && e.key !== " ") {
                                          return;
                                        }
                                        e.preventDefault();
                                        e.stopPropagation();
                                        void togglePodPin(template._id).catch(
                                          (error) => {
                                            console.error(
                                              "Failed to toggle pod pin:",
                                              error,
                                            );
                                          },
                                        );
                                      }}
                                    >
                                      <FontAwesomeIcon
                                        icon={
                                          isPinned
                                            ? faThumbtackSolid
                                            : faThumbtack
                                        }
                                        className={`w-3 h-3 `}
                                      />
                                    </div>
                                  )
                                }
                                icon={
                                  <FontAwesomeIcon
                                    icon={getPodIconFromTemplate(template)}
                                    fixedWidth
                                  />
                                } // Use the utility function
                                themeBaseTextColor={currentTheme?.baseTextColor}
                              />
                            </div>
                          );
                        })}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* WORKERS Section */}
            {!permissions.single_app && hasPermission("worker") && (
              <div className="">
                <div className={`px-4 py-1 pb-0 pt-0 pr-0 group/icons`}>
                  <div
                    role="button"
                    tabIndex={0}
                    className={`relative flex items-center gap-3 px-2.5 min-h-[32px] rounded-lg group cursor-pointer ${isCollapsed ? " pr-0" : ""
                      } ${currentTheme?.baseTextColor === "light"
                        ? "text-white/60 hover:text-white"
                        : "text-slate-500 hover:text-slate-800"
                      }`}
                    onClick={handleWorkersHeaderClick}
                    onKeyDown={(e) =>
                      activateOnEnterOrSpace(e, handleWorkersHeaderClick)
                    }
                    title="WORKERS"
                  >
                    <div className="flex-shrink-0 relative w-5 self-center flex items-center justify-center icon-clickable">
                      <div className="absolute text-lg">
                        <div className="relative ">
                          <FontAwesomeIcon
                            icon={faUsers}
                            className={`w-5 h-5 ${isCollapsed ? "" : "transition-opacity"
                              } ${isWorkersExpanded && !isCollapsed
                                ? " opacity-0"
                                : "opacity-100"
                              }

                            ${!isWorkersExpanded
                                ? "opacity-100 group-hover/icons:opacity-0"
                                : ""
                              }
                            `}
                            fixedWidth
                          />
                          {!isCollapsed && (
                            <FontAwesomeIcon
                              icon={faChevronDown}
                              role="button"
                              tabIndex={0}
                              onClick={handleWorkersIconClick}
                              onKeyDown={(e) => {
                                if (e.key !== "Enter" && e.key !== " ") {
                                  return;
                                }
                                e.preventDefault();
                                e.stopPropagation();
                                setIsWorkersExpanded(!isWorkersExpanded);
                              }}
                              className={`absolute flex items-center justify-center inset-0 left-1 opacity-0 group-hover:opacity-100 text-xs transition-all ${isWorkersExpanded
                                ? "rotate-180 opacity-100 top-2"
                                : "top-1.5"
                                }`}
                              fixedWidth
                            />
                          )}
                        </div>
                      </div>
                    </div>
                    {!isCollapsed && (
                      <div
                        className={`${currentTheme?.baseTextColor === "light"
                          ? "text-white/60"
                          : "text-slate-500"
                          } flex-1 min-w-0 overflow-hidden transition-opacity duration-400 opacity-100`}
                      >
                        <span className="relative top-[-1px] text-[11px] font-mono uppercase tracking-widest inline-block whitespace-nowrap">
                          WORKERS
                        </span>
                      </div>
                    )}
                  </div>
                </div>

                {(isWorkersExpanded || isCollapsed) && (
                  <div className="flex-grow mb-4">
                    <div
                      className={`h-full overflow-y-auto scrollbar-thin scrollbar-thumb-gray-300 dark:scrollbar-thumb-gray-700 px-4 pr-0 flex flex-col `}
                    >
                      {pinnedWorkersLoading && (
                        <>
                          <WorkerSkeleton collapsed={isCollapsed} />
                          <WorkerSkeleton collapsed={isCollapsed} />
                        </>
                      )}
                      {pinnedWorkersError && (
                        <div className="text-xs text-white/60 pl-7 py-1">
                          Failed to load pinned workers
                        </div>
                      )}
                      {!pinnedWorkersLoading &&
                        !pinnedWorkersError &&
                        workersToShow.length === 0 && (
                          <NavLink
                            to="/worker/directory"
                            label="Explore Workers"
                            isActive={location.pathname.startsWith(
                              "/worker/directory"
                            )}
                            collapsed={isCollapsed}
                            icon={
                              <div className="w-6 h-6 rounded-full bg-slate-500/50 flex items-center justify-center">
                                <FontAwesomeIcon
                                  icon={faPlus}
                                  className="w-3 h-3 text-white"
                                />
                              </div>
                            }
                            themeBaseTextColor={currentTheme?.baseTextColor}
                          />
                        )}
                      {!pinnedWorkersLoading &&
                        !pinnedWorkersError &&
                        workersToShow.length > 0 &&
                        workersToShow.map((worker) => {
                          const isPinned = worker.is_pinned ?? true;
                          return (
                            <div
                              key={worker._id}
                              className="relative group/navitem"
                            >
                              <NavLink
                                to={`/worker/${worker._id}`}
                                label={worker.name}
                                isActive={location.pathname.startsWith(
                                  `/worker/${worker._id}`
                                )}
                                collapsed={isCollapsed}
                                icon={
                                  <WorkerAvatar seed={worker.name} size={22} />
                                }
                                themeBaseTextColor={currentTheme?.baseTextColor}
                              />
                              {!isCollapsed && (
                                <div
                                  role="button"
                                  tabIndex={0}
                                  className={`absolute right-2 top-1/2 -translate-y-1/2 transition-opacity cursor-pointer p-1 rounded ${isPinned
                                    ? "opacity-100"
                                    : "opacity-0 group-hover/navitem:opacity-100"
                                    } ${currentTheme?.baseTextColor === "light"
                                      ? "text-white/60 hover:text-white"
                                      : "text-slate-500 hover:text-slate-800"
                                    }`}
                                  onClick={async (e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    try {
                                      await toggleWorkerPin(worker._id);
                                    } catch (error) {
                                      console.error(
                                        "Failed to toggle worker pin:",
                                        error
                                      );
                                    }
                                  }}
                                  onKeyDown={(e) => {
                                    if (e.key !== "Enter" && e.key !== " ") {
                                      return;
                                    }
                                    e.preventDefault();
                                    e.stopPropagation();
                                    void toggleWorkerPin(worker._id).catch(
                                      (error) => {
                                        console.error(
                                          "Failed to toggle worker pin:",
                                          error,
                                        );
                                      },
                                    );
                                  }}
                                >
                                  <FontAwesomeIcon
                                    icon={
                                      isPinned ? faThumbtackSolid : faThumbtack
                                    }
                                    className={`w-3 h-3 `}
                                  />
                                </div>
                              )}
                            </div>
                          );
                        })}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Apps Section */}
            {shouldShowAppsSection(permissions.single_app ?? false, hasPermission) && (
              <div className="">
                <div className={`px-4 py-1 pb-0 pt-0 pr-0 group/icons`}>
                  <div
                    role="button"
                    tabIndex={0}
                    className={`relative flex items-center gap-3 px-2.5 min-h-[32px] rounded-lg group cursor-pointer ${isCollapsed ? " pr-0" : ""
                      } ${currentTheme?.baseTextColor === "light"
                        ? "text-white/60 hover:text-white"
                        : "text-slate-500 hover:text-slate-800"
                      }`}
                    onClick={() => setIsAppsExpanded(!isAppsExpanded)}
                    onKeyDown={(e) =>
                      activateOnEnterOrSpace(e, () =>
                        setIsAppsExpanded(!isAppsExpanded),
                      )
                    }
                    title="Apps"
                  >
                    <div className="flex-shrink-0 relative w-5 self-center flex items-center justify-center icon-clickable">
                      <div className="absolute text-lg">
                        <div className="relative ">
                          {!isCollapsed && (
                            <FontAwesomeIcon
                              icon={faGrid2}
                              className={`w-5 h-5 ${isCollapsed ? "" : "transition-opacity"
                                } ${isAppsExpanded && !isCollapsed
                                  ? " opacity-0"
                                  : "opacity-100"
                                }

                              ${!isAppsExpanded
                                  ? "opacity-100 group-hover/icons:opacity-0"
                                  : ""
                                }
                              `}
                              fixedWidth
                            />
                          )}
                          {!isCollapsed && (
                            <FontAwesomeIcon
                              icon={faChevronDown}
                              className={`absolute flex items-center justify-center inset-0 left-1 opacity-0 group-hover:opacity-100 text-xs transition-all ${isAppsExpanded
                                ? "rotate-180 opacity-100 top-2"
                                : "top-1.5"
                                }`}
                              fixedWidth
                            />
                          )}
                        </div>
                      </div>
                    </div>
                    {!isCollapsed && (
                      <div
                        className={`${currentTheme?.baseTextColor === "light"
                          ? "text-white/60"
                          : "text-slate-500"
                          } flex-1 min-w-0 overflow-hidden transition-opacity duration-400 opacity-100`}
                      >
                        <span className="relative top-[-1px] text-[11px] font-mono uppercase tracking-widest inline-block whitespace-nowrap">
                          Apps
                        </span>
                      </div>
                    )}
                  </div>
                </div>

                {(isAppsExpanded || isCollapsed) && (
                  <div className="flex-grow mb-4">
                    <div
                      className={`h-full overflow-y-auto scrollbar-thin scrollbar-thumb-gray-300 dark:scrollbar-thumb-gray-700 px-4 pr-0 flex flex-col `}
                    >
                      {permissionedSpecializedApps.map((app) => (
                        <NavLink
                          key={app.path}
                          to={app.path}
                          label={app.title}
                          isActive={location.pathname.startsWith(app.path)}
                          collapsed={isCollapsed}
                          icon={
                            <FontAwesomeIcon icon={app.icon} fixedWidth />
                          }
                          themeBaseTextColor={currentTheme?.baseTextColor}
                        />
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
        {/* FIXED BOTTOM AREA: settings/controls only */}
        <div className="flex-shrink-0">
          <div className="p-4 pr-0 pb-6.5 flex gap-2 items-center relative">
            {!isCollapsed && (
              <SettingsPopover
                isCollapsed={isCollapsed}
                availableThemes={availableThemes}
                currentTheme={currentTheme}
                onSelectTheme={onSelectTheme}
                closeIcon={closeIcon}
                companyId={companyId}
                onToggleLayout={onToggleLayout}
              />
            )}
            {isCollapsed && (
              <div className="mt-2 flex justify-center">
                <SettingsPopover
                  isCollapsed={isCollapsed}
                  availableThemes={availableThemes}
                  currentTheme={currentTheme}
                  onSelectTheme={onSelectTheme}
                  closeIcon={closeIcon}
                  companyId={companyId}
                  onToggleLayout={onToggleLayout}
                />
              </div>
            )}
          </div>
        </div>
      </nav>
      {!permissions?.single_app && !forceExpanded && (
        <div className="absolute group-hover/collapse:opacity-100 opacity-0 transition-opacity duration-200 top-0 bottom-0 z-20 -right-4 w-8 flex items-center justify-center">
          <button
            onClick={() => setIsCollapsed(!isCollapsedState)}
            onMouseOver={() => setIsCollapseHovered(true)}
            onMouseLeave={() => setIsCollapseHovered(false)}
            className={`flex items-center justify-center cursor-pointer h-16 ${currentTheme?.baseTextColor === "light"
              ? "text-white/60 hover:text-white"
              : "text-slate-900/40 hover:text-slate-900/70"
              } rounded-lg p-1`}
            style={{
              position: "absolute",
              top: "50%",
              left: 0,
              right: 0,
              transform: "translateY(-50%)",
            }}
            title={isCollapsed ? "Expand sidebar" : "Collapse sidebar"}
          >
            <CollapseIcon
              isCollapsed={isCollapsed}
              isHovered={isCollapseHovered}
              className="w-5 h-10 left-[7px] relative"
            />
          </button>
        </div>
      )}
    </div>
  );
}
